import React, { useState, useEffect } from 'react';
import Select from 'react-select';
import { toast } from 'react-toastify';
import { getCloudRunsForProject, createProduct } from '../services/api';
import selectStyles from '../shared/selectStyles';
import './AddProductModal.css';

function AddProductModal({ gcpProjects, onClose, onCreated }) {
  const [productName, setProductName] = useState('');
  const [selectedProject, setSelectedProject] = useState(null);
  const [cloudRuns, setCloudRuns] = useState([]);
  const [selectedCloudRuns, setSelectedCloudRuns] = useState([]);
  const [loadingCloudRuns, setLoadingCloudRuns] = useState(false);
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    if (selectedProject) {
      fetchCloudRuns(selectedProject.value);
    } else {
      setCloudRuns([]);
    }
  }, [selectedProject]);

  const fetchCloudRuns = async (projectId) => {
    setLoadingCloudRuns(true);
    try {
      const res = await getCloudRunsForProject(projectId);
      setCloudRuns(res.data);
    } catch (err) {
      toast.error('Failed to fetch Cloud Run services');
      setCloudRuns([]);
    }
    setLoadingCloudRuns(false);
  };

  const projectOptions = gcpProjects.map((p) => ({
    value: p._id,
    label: `${p.projectName} (${p.projectId})`,
    projectName: p.projectName,
  }));

  const cloudRunOptions = cloudRuns.map((cr) => ({
    value: `${cr.name}|${cr.region}`,
    label: `${cr.name} (${cr.region})`,
    serviceName: cr.name,
    region: cr.region,
  }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!productName.trim()) {
      toast.error('Product name is required');
      return;
    }

    setCreating(true);
    try {
      const cloudRunsData = selectedCloudRuns.map((cr) => ({
        serviceName: cr.serviceName,
        region: cr.region,
        gcpProjectId: selectedProject.value,
        gcpProjectName: selectedProject.projectName,
      }));

      const res = await createProduct({
        name: productName.trim(),
        cloudRuns: cloudRunsData,
      });

      toast.success(`Product "${productName}" created`);
      onCreated(res.data);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to create product');
    }
    setCreating(false);
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal add-product-modal" onClick={(e) => e.stopPropagation()}>
        <h2>Add New Product</h2>
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Product Name</label>
            <input
              type="text"
              placeholder="Enter product name..."
              value={productName}
              onChange={(e) => setProductName(e.target.value)}
              autoFocus
            />
          </div>

          <div className="form-group">
            <label>GCP Project</label>
            {gcpProjects.length === 0 ? (
              <p className="hint-text">
                No GCP projects configured. Go to GCP Projects to add one first.
              </p>
            ) : (
              <Select
                styles={selectStyles}
                options={projectOptions}
                value={selectedProject}
                onChange={setSelectedProject}
                placeholder="Select a GCP project..."
                isClearable
              />
            )}
          </div>

          {selectedProject && (
            <div className="form-group">
              <label>Cloud Run Services</label>
              {loadingCloudRuns ? (
                <div className="loading-inline">
                  <div className="spinner" style={{ width: 20, height: 20 }}></div>
                  <span>Fetching Cloud Run services...</span>
                </div>
              ) : (
                <Select
                  styles={selectStyles}
                  options={cloudRunOptions}
                  value={selectedCloudRuns}
                  onChange={setSelectedCloudRuns}
                  placeholder="Select Cloud Run services..."
                  isMulti
                  closeMenuOnSelect={false}
                  noOptionsMessage={() => 'No Cloud Run services found'}
                />
              )}
            </div>
          )}

          <div className="modal-actions">
            <button type="button" className="btn btn-secondary" onClick={onClose}>
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={creating || !productName.trim()}
            >
              {creating ? 'Creating...' : 'Create Product'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default AddProductModal;
